import React from 'react'
import { Head, router } from '@inertiajs/react'
import {
  Container, Typography, Box, Button, Paper, Chip, Stack, Divider,
  ToggleButton, ToggleButtonGroup, Link as MuiLink,
} from '@mui/material'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import CancelIcon from '@mui/icons-material/Cancel'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'

interface Reviewer {
  id: number
  name: string
}

interface Suggestion {
  id: number
  status: 'pending' | 'approved' | 'rejected'
  source_type: string | null
  source_title: string | null
  source_url: string | null
  visited_at: string | null
  notes: string | null
  rejection_reason: string | null
  reviewed_at: string | null
  reviewer: Reviewer | null
  submitted_by: Reviewer
  group: { id: number; name: string; color: string | null }
  location: { id: number; name: string; address: string | null; category: string | null }
  created_at: string
}

interface Props {
  suggestions: Suggestion[]
  counts: { pending: number; approved: number; rejected: number }
  filters: { status: string | null }
}

const STATUS_COLORS: Record<Suggestion['status'], 'warning' | 'success' | 'default'> = {
  pending: 'warning',
  approved: 'success',
  rejected: 'default',
}

function formatDate(iso: string | null): string {
  if (!iso) return ''
  return new Date(iso).toLocaleDateString()
}

export default function GroupLocationSuggestionsIndex({ suggestions, counts, filters }: Props) {
  const activeStatus = filters.status || 'all'

  const changeStatus = (_: unknown, value: string | null) => {
    const status = value && value !== 'all' ? value : undefined
    router.get('/group_location_suggestions', status ? { status } : {}, { preserveState: true })
  }

  const approve = (s: Suggestion) => {
    router.patch(`/group_location_suggestions/${s.id}/approve`, { status: filters.status || undefined })
  }

  const reject = (s: Suggestion) => {
    const reason = window.prompt('Optional reason for rejecting this suggestion:') ?? undefined
    router.patch(`/group_location_suggestions/${s.id}/reject`, {
      status: filters.status || undefined,
      rejection_reason: reason,
    })
  }

  return (
    <>
      <Head title="Location suggestions" />
      <Container maxWidth="lg" sx={{ py: { xs: 3, md: 4 }, px: { xs: 2, md: 4 } }}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h5">Location suggestions</Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Review member-submitted places before they are linked to a group.
          </Typography>
        </Box>

        <ToggleButtonGroup
          exclusive
          size="small"
          value={activeStatus}
          onChange={changeStatus}
          sx={{ mb: 3, flexWrap: 'wrap' }}
        >
          <ToggleButton value="all">All</ToggleButton>
          <ToggleButton value="pending">Pending ({counts.pending})</ToggleButton>
          <ToggleButton value="approved">Approved ({counts.approved})</ToggleButton>
          <ToggleButton value="rejected">Rejected ({counts.rejected})</ToggleButton>
        </ToggleButtonGroup>

        {suggestions.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              No suggestions {activeStatus !== 'all' ? `with status “${activeStatus}”` : 'yet'}.
            </Typography>
          </Paper>
        ) : (
          <Stack spacing={2}>
            {suggestions.map((s) => (
              <Paper key={s.id} sx={{ p: { xs: 2, md: 2.5 } }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
                  <Box sx={{ minWidth: 0 }}>
                    <Stack direction="row" spacing={1} sx={{ mb: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
                      <Chip
                        size="small"
                        label={s.group.name}
                        sx={{
                          backgroundColor: s.group.color || 'rgba(123, 91, 214,0.15)',
                          color: '#fff',
                          fontWeight: 700,
                        }}
                      />
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                        {s.location.name}
                      </Typography>
                      <Chip size="small" label={s.status} color={STATUS_COLORS[s.status]} variant="outlined" />
                    </Stack>
                    {s.location.address && (
                      <Typography variant="body2" color="text.secondary">{s.location.address}</Typography>
                    )}
                    {s.location.category && (
                      <Typography variant="caption" color="text.disabled">{s.location.category}</Typography>
                    )}
                  </Box>

                  {s.status === 'pending' && (
                    <Stack direction="row" spacing={1}>
                      <Button
                        size="small"
                        variant="contained"
                        color="success"
                        startIcon={<CheckCircleIcon />}
                        onClick={() => approve(s)}
                      >
                        Approve
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        color="error"
                        startIcon={<CancelIcon />}
                        onClick={() => reject(s)}
                      >
                        Reject
                      </Button>
                    </Stack>
                  )}
                </Box>

                <Divider sx={{ my: 1.5 }} />

                <Stack spacing={0.5}>
                  <Typography variant="caption" color="text.secondary">
                    Submitted by <strong>{s.submitted_by.name}</strong> on {formatDate(s.created_at)}
                  </Typography>
                  {s.source_type && (
                    <Typography variant="body2">
                      Source: {s.source_title || s.source_type}
                      {s.source_url && (
                        <MuiLink href={s.source_url} target="_blank" rel="noopener" sx={{ ml: 1, display: 'inline-flex', alignItems: 'center' }}>
                          link <OpenInNewIcon sx={{ fontSize: 14, ml: 0.25 }} />
                        </MuiLink>
                      )}
                    </Typography>
                  )}
                  {s.visited_at && (
                    <Typography variant="body2">Visited: {formatDate(s.visited_at)}</Typography>
                  )}
                  {s.notes && (
                    <Typography variant="body2" color="text.secondary">“{s.notes}”</Typography>
                  )}
                  {s.status === 'approved' && s.reviewer && (
                    <Typography variant="caption" color="success.main">
                      Approved by {s.reviewer.name} on {formatDate(s.reviewed_at)}
                    </Typography>
                  )}
                  {s.status === 'rejected' && (
                    <Typography variant="caption" color="text.disabled">
                      Rejected{s.reviewer ? ` by ${s.reviewer.name}` : ''} on {formatDate(s.reviewed_at)}
                      {s.rejection_reason ? ` — ${s.rejection_reason}` : ''}
                    </Typography>
                  )}
                </Stack>
              </Paper>
            ))}
          </Stack>
        )}
      </Container>
    </>
  )
}
